import React, { useReducer, useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import VirtualizeGrouped, {
  formatOptions
} from '../Components/CustomUIElements/VirtualizedGroupedAutocomplete';
import { Box, Checkbox, FormControlLabel, Alert, CircularProgress } from '@mui/material';
import { FileWithPath } from 'react-dropzone';
import AttachmentSelectorAndPreview from '../Components/CustomUIElements/AttachmentSelectorAndPreview';
import { FormTextField } from '../Components/CustomUIElements/FormTextField';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import StyledModal from '../Components/GenericModal/StyledModal';
import HtmlEditor from '../Components/Utilities/HtmlEditor';
import DelaySelectWrapper from '../EditJob/Automation/shared/DelaySelectWrapper';
import Api from './API';
import StyledSnackbar, { ISnackbarInput } from '../Components/CustomUIElements/StyledSnackbar';
import { EmailModalState } from './types';
import { InitialEmailModalState, EmailModalReducer } from './emailReducer';
import { validateEmailForm } from './emailUtils';
import { handleAPIRequest } from '../Components/Utilities/handleAPIRequest';

const EmailCandidateModal = ({
  selectedPoolIds,
  isOpen,
  onClose
}: {
  selectedPoolIds: number[];
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [state, dispatch] = useReducer(EmailModalReducer, InitialEmailModalState);
  const { formData, errors, snackbar } = state;
  const [templateAttachments, setTemplateAttachments] = useState<any[]>([]);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET_FORM' });
      setTemplateAttachments([]);
      setIsLoadingTemplate(false);
    }
  }, [isOpen]);

  // Fetch email templates (cached globally)
  const emailTemplates = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      try {
        const response = await Api.getEmailTemplates();
        const responseData = response as any;

        if (!responseData.res?.email_templates) {
          throw new Error('Invalid response format from API');
        }

        return {
          email_templates: formatOptions(responseData.res.email_templates),
          placeholders: responseData.res.placeholders || 'No placeholders available'
        };
      } catch (error) {
        console.error('Error fetching email templates:', error);
        throw new Error('Failed to fetch email templates');
      }
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000
  });

  // Send emails mutation
  const { mutate: sendEmails, isLoading: isSendingEmails } = useMutation({
    mutationFn: async () => {
      const validationErrors = validateEmailForm(formData);
      if (Object.keys(validationErrors).length > 0) {
        dispatch({ type: 'SET_ERRORS', payload: validationErrors });
        throw new Error('Please fix the form errors before sending');
      }

      if (selectedPoolIds.length === 0) {
        throw new Error('No candidate sourcing pools selected');
      }

      const response = await Api.bulkEmailCandidateSourcingPools(selectedPoolIds, {
        email_template_id: Number(formData.selectedTemplate!.split(' -')[0]),
        subject: formData.subject.trim(),
        body: formData.body.trim()
      });

      return response as { res: { message: string }; resHead: { [key: string]: string } };
    },
    onSuccess: (data) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: data.res.message,
          state: 'success'
        }
      });

      setTimeout(() => onClose(), 1500);
    },
    onError: (error: any) => {
      let errorMessage = 'Failed to send emails';

      if (error.errors) {
        errorMessage = error.errors;
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: errorMessage,
          state: 'error'
        }
      });
    }
  });

  // Handlers
  const resetTemplateData = useCallback(() => {
    dispatch({
      type: 'SET_FORM_DATA',
      payload: {
        selectedTemplate: null,
        body: '',
        subject: '',
        attachments: []
      }
    });
    setTemplateAttachments([]);
    setIsLoadingTemplate(false);
  }, []);

  const handleSelectTemplate = useCallback(
    (value: string | null) => {
      if (!value) {
        resetTemplateData();
        return;
      }

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          selectedTemplate: value,
          body: '',
          subject: '',
          attachments: []
        }
      });

      setTemplateAttachments([]);
      setIsLoadingTemplate(true);

      const templateId = value.split(' -')[0];
      handleAPIRequest({
        url: `/api/v4/searches/email_templates/${templateId}`,
        method: 'GET',
        onSuccess: (response) => {
          dispatch({
            type: 'SET_FORM_DATA',
            payload: {
              body: response?.body || '',
              subject: response?.subject || '',
              attachments: []
            }
          });
          setTemplateAttachments(response?.email_attachments || []);
          setIsLoadingTemplate(false);
        },
        onFailure: () => {
          resetTemplateData();
        },
        onFinally: () => {
          setIsLoadingTemplate(false);
        }
      });
    },
    [resetTemplateData]
  );

  const handleFormChange = useCallback((field: keyof EmailModalState['formData'], value: any) => {
    dispatch({
      type: 'SET_FORM_DATA',
      payload: { [field]: value }
    });
  }, []);

  const handleClose = useCallback(() => {
    if (!isSendingEmails) {
      onClose();
    }
  }, [isSendingEmails, onClose]);

  const handleSend = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
    sendEmails();
  }, [sendEmails]);

  const hasFormErrors = Object.keys(errors).length > 0;

  return (
    <StyledModal
      isOpen={isOpen}
      disableEnforceFocus
      handleClose={handleClose}
      label="Email Candidate Sourcing Pools"
      modalTitle={`Email Candidate Sourcing Pools`}
      styleOverrides={{
        maxWidth: '900px',
        maxHeight: '1100px'
      }}
      subHeaderStyling={false}
    >
      <Box sx={sharedClasses.mainModalContent}>
        <Box role="form" id="email-pool-candidates-form" aria-label="Email Pool Candidates form">
          {/* Template Selection */}
          <Box sx={sharedClasses.actionModalFormLine}>
            <VirtualizeGrouped
              value={formData.selectedTemplate}
              options={emailTemplates.data?.email_templates || []}
              onChange={handleSelectTemplate}
              styles={{
                ...sharedClasses.formAutocomplete,
                width: '600px',
                marginTop: '20px'
              }}
              label="Template"
              loading={emailTemplates.isLoading}
              loadingText="Loading template list..."
              error={errors.template}
            />
          </Box>

          {/* Exclude Applicants Checkbox */}
          <Box sx={sharedClasses.actionModalFormLine}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={sharedClasses.formCheckbox}
                  checked={formData.excludeApplicants}
                  onChange={(e) => handleFormChange('excludeApplicants', e.target.checked)}
                />
              }
              label="Exclude applicants who have received this email template"
              sx={sharedClasses.checkboxLabel}
            />
          </Box>

          {/* Delay Selection */}
          <DelaySelectWrapper
            delay={formData.delay}
            setDelay={(delay) => handleFormChange('delay', delay)}
          />

          {/* Subject Field */}
          <Box sx={sharedClasses.actionModalFormLine}>
            <FormTextField
              id="email-subject"
              label="Subject"
              value={formData.subject}
              styles={{ width: '600px' }}
              onChange={(e) => handleFormChange('subject', e.target.value)}
              error={errors.subject}
              required
            />
          </Box>

          {/* Body Editor */}
          <Box sx={sharedClasses.modalSection}>
            <Box sx={sharedClasses.formLabel}>Body</Box>
            {formData.selectedTemplate && isLoadingTemplate ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Box sx={{ ml: 2 }}>Loading template...</Box>
              </Box>
            ) : (
              <>
                <HtmlEditor
                  body={formData.body}
                  onBlur={(body) => handleFormChange('body', body)}
                />
                <Box sx={sharedClasses.placeholderText}>
                  Available placeholders are: {emailTemplates.data?.placeholders}
                </Box>
                {errors.body && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.body}
                  </Alert>
                )}
              </>
            )}
          </Box>

          {/* Attachments */}
          <AttachmentSelectorAndPreview
            attachments={formData.attachments}
            setAttachments={(attachments: FileWithPath[]) =>
              handleFormChange('attachments', attachments)
            }
            templateAttachments={templateAttachments}
            selectedPoolIds={selectedPoolIds}
          />

          {/* Form Errors Summary */}
          {hasFormErrors && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Please fix the following errors:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Box>
      </Box>

      {/* Footer Buttons */}
      <ModalFooterButtons
        primaryButtonText="Send"
        primaryButtonCallback={handleSend}
        primaryButtonID="send-pool-candidate-emails"
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleClose}
        secondaryButtonID="cancel-pool-candidate-emails"
        isLoading={isSendingEmails}
      />

      {/* Snackbar */}
      <StyledSnackbar
        message={snackbar.message}
        state={snackbar.state}
        setSnackbarState={(snackbarState: ISnackbarInput) =>
          dispatch({ type: 'SET_SNACKBAR', payload: snackbarState })
        }
      />
    </StyledModal>
  );
};

export default EmailCandidateModal;
